import { Check, Copy, ExternalLink, PlugZap, Repeat, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";
import { colocaToast } from "@/shared/utils/toast";

import { corridorTripleKey } from "../../constants/paymentRoutes.constants";
import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { CorridorRouteCell } from "../../types/paymentRoutes.types";
import {
  providerCoverageChips,
  ROUTE_STATUS_CONSEQUENCE_KEY,
  ROUTE_STATUS_LABEL_KEY,
  ROUTE_STATUS_PILL_VARIANT,
} from "../../utils/routeFormat";

export interface RouteDetailDrawerProps {
  /** Cell being inspected — always one with a `route`. */
  cell: CorridorRouteCell;
  open: boolean;
  onClose: () => void;
  onSwitch: (cell: CorridorRouteCell) => void;
  onDelete: (cell: CorridorRouteCell) => void;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

/**
 * Label + value row for short, scalar values.
 *
 * The value **wraps** by default instead of truncating. The panel is `max-w-md`, so a value that is a
 * sentence rather than a scalar does not fit on one line next to its label, and an ellipsis there hides
 * exactly the part that carries the meaning. `truncate` is opt-in and belongs only to values that are long by
 * nature and recoverable in full — the route id, which ships a copy button.
 *
 * `items-start` so a wrapped value aligns with the label's first line.
 */
function MetaRow({
  label,
  value,
  copyValue,
  mono,
  truncate,
}: {
  label: string;
  value: ReactNode;
  copyValue?: string;
  mono?: boolean;
  /** Clip to one line with an ellipsis. Only for values whose full text is available another way. */
  truncate?: boolean;
}) {
  const { t } = useTranslations("paymentRoutes");
  if (value == null) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="shrink-0 pt-0.5 text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-start gap-1">
        <span
          className={cn(
            "text-right text-sm text-foreground",
            mono ? "font-mono text-xs" : "font-medium",
            truncate ? "truncate" : "break-words",
          )}
        >
          {value}
        </span>
        {copyValue && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            className="-mt-1 shrink-0"
            aria-label={t("drawer_copy_id")}
            onClick={() => {
              void navigator.clipboard
                .writeText(copyValue)
                .then(() => colocaToast.success(t("drawer_copied")))
                .catch(() => undefined);
            }}
          />
        )}
      </span>
    </div>
  );
}

/**
 * A verdict, not a field: a full-width label line with the statement below it.
 *
 * The capability check is the difference between a payout that dispatches and one that reserves funds and
 * strands as `CREATED`, and its wording is a sentence — squeezing it into the right half of a label/value row
 * is what made it unreadable. Colour is never the only signal: the icon and the sentence both carry it.
 */
function VerdictBlock({ label, statement, ok }: { label: string; statement: string; ok: boolean }) {
  return (
    <div className="py-1.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-0.5 flex items-start gap-1.5 text-sm font-medium",
          ok ? "text-success" : "text-warning",
        )}
      >
        {ok ? (
          <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <PlugZap className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        )}
        <span className="min-w-0 break-words">{statement}</span>
      </p>
    </div>
  );
}

/**
 * Read-only detail of one corridor's route, plus its two write actions.
 *
 * The provider section never names the provider (rule 12): it shows the corridors that provider's adapter
 * declares and whether this corridor is among them — which is also the answer to "will payouts here dispatch
 * or strand", so the capability line is a first-class field rather than a footnote.
 *
 * The fees deep link exists because a routed corridor is still dead without a fee rule: the two halves of a
 * working corridor live on two screens, and this is the shortest path between them.
 */
export function RouteDetailDrawer({ cell, open, onClose, onSwitch, onDelete }: RouteDetailDrawerProps) {
  const { t, activeLanguage } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();
  const route = cell.route;

  const coverage = route ? providerCoverageChips(route.provider_code) : [];
  const feesQuery = new URLSearchParams({
    tab: "commissions",
    corridor: corridorTripleKey(cell),
    direction: cell.direction,
  }).toString();

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${cell.country} · ${formatRail(cell.rail)} · ${cell.currency}`}
      description={t("drawer_subtitle")}
      size="md"
      footer={
        route && (
          <div className="flex w-full flex-wrap justify-end gap-2">
            <ColocaButton variant="default" icon={Repeat} onClick={() => onSwitch(cell)}>
              {t("action_switch")}
            </ColocaButton>
            <ColocaButton variant="destructive" icon={Trash2} onClick={() => onDelete(cell)}>
              {t("action_delete")}
            </ColocaButton>
          </div>
        )
      }
    >
      {/* Multiple direct children on purpose: with a single root child `ColocaDrawer` sticky-pins its last
          element to the bottom, which would strand the fees link over the footer. */}
      <section className="mb-5">
        <SectionTitle>{t("drawer_section_corridor")}</SectionTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <ColocaBadge variant="default">{cell.country}</ColocaBadge>
          <ColocaBadge variant="secondary" className="font-sans">
            {formatRail(cell.rail)}
          </ColocaBadge>
          <span className="font-mono text-xs text-muted-foreground">{cell.currency}</span>
          <ColocaBadge variant="default" className="font-sans">
            {labels.direction[cell.direction]}
          </ColocaBadge>
          {cell.offCatalog && (
            <ColocaBadge variant="secondary" className="border-dashed font-sans text-muted-foreground">
              {t("offcatalog_badge")}
            </ColocaBadge>
          )}
        </div>

        <div className="mt-3 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">{t("drawer_field_status")}</span>
            <ColocaStatusPill variant={ROUTE_STATUS_PILL_VARIANT[cell.status]} size="sm">
              {t(ROUTE_STATUS_LABEL_KEY[cell.status])}
            </ColocaStatusPill>
          </div>
          <p className="text-xs text-muted-foreground">{t(ROUTE_STATUS_CONSEQUENCE_KEY[cell.status])}</p>
        </div>

        {route && (
          <div className="mt-3 border-t border-border pt-2">
            <MetaRow label={t("drawer_field_model")} value={labels.model[route.economic_model]} />
            <p className="pb-1 text-xs text-muted-foreground">{labels.modelHelp[route.economic_model]}</p>
          </div>
        )}
      </section>

      {route && (
        <section className="mb-5">
          <SectionTitle>{t("drawer_section_provider")}</SectionTitle>
          {coverage.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {coverage.map((label) => (
                <ColocaBadge key={label} variant="secondary" className="font-sans">
                  {label}
                </ColocaBadge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-warning">{t("provider_unknown")}</p>
          )}
          {/* Caption, not a labelled row: the badges above already ARE the coverage, so a "Provider coverage"
              label whose value counts routed corridors was naming the wrong thing. */}
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t("provider_corridors", { count: cell.providerCorridorCount })}
          </p>
          {/* No alert repeating `consequence_unsupported` here: the status block above already states it
              verbatim, and the verdict below names the cause. Three renderings of one fact read as padding. */}
          <div className="mt-1">
            <VerdictBlock
              label={t("drawer_field_capability")}
              statement={cell.providerDeclaresCorridor ? t("capability_ok") : t("capability_missing")}
              ok={cell.providerDeclaresCorridor}
            />
          </div>
        </section>
      )}

      {route && (
        <section className="mb-5">
          <SectionTitle>{t("drawer_section_audit")}</SectionTitle>
          <MetaRow label={t("drawer_field_id")} value={route.id} copyValue={route.id} mono truncate />
          <MetaRow
            label={t("drawer_field_created")}
            value={formatDateTime(route.created_at, { locale: activeLanguage })}
          />
          <MetaRow
            label={t("drawer_field_updated")}
            value={formatDateTime(route.updated_at, { locale: activeLanguage })}
          />
        </section>
      )}

      <Link
        to={`/app/payments-config/fees?${feesQuery}`}
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <ExternalLink className="size-3.5" aria-hidden="true" />
        {t("drawer_link_fees")}
      </Link>
    </ColocaDrawer>
  );
}
